import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ApiClientService } from '../services/api-client.service';
import { TranslationService } from '../services/translation.service';

/** Shared across routes so the language is resolved once per app load, not per navigation. */
let resolution: Promise<void> | null = null;

/**
 * Holds route activation until the shell language is known, so no screen renders in the
 * wrong language and then swaps. Never blocks access: it only delays.
 *
 * Deliberately a resolver and not a guard: Angular starts every `canActivate` guard of a
 * route at the same time, so a guard here would call the protected API while `MsalGuard`
 * is still initialising MSAL. `MsalInterceptor` would then find no token yet and kick off
 * its own interactive redirect, colliding with the guard's login
 * (`interaction_in_progress`) and leaving the user unable to sign in. Resolvers run only
 * after every guard has passed, so the token is there by the time we call.
 */
export const languageResolver: ResolveFn<void> = async () => {
    const apiClient = inject(ApiClientService);
    const translationService = inject(TranslationService);

    resolution ??= resolveLanguage(apiClient, translationService);
    await resolution;
};

/**
 * The user's Berghaus Connect language wins on every load, unless they picked one in this tab.
 */
async function resolveLanguage(
    apiClient: ApiClientService,
    translationService: TranslationService
): Promise<void> {
    if (translationService.hasSessionOverride()) {
        return;
    }

    try {
        const response = await firstValueFrom(apiClient.getUserLanguage());
        if (response.languageId) {
            await translationService.setLanguageFromApi(response.languageId);
        }
    } catch {
        // Best-effort: keep the browser-detected language so the error UI still has strings.
    }
}
